import React, {useCallback, useMemo, useRef, useState} from 'react';
import DataGrid, {
  Column,
  Editing,
  FilterRow,
  Scrolling,
  Toolbar,
  Item
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import CustomStore from 'devextreme/data/custom_store';
import notify from 'devextreme/ui/notify';
import {apiRequest} from 'api/async';
import {vars} from 'utils/variables';
import {Popup} from 'devextreme-react/popup';
import {Form, SimpleItem} from 'devextreme-react/form';
import './payment-system-params.scss';

const {APP_CODES: {SUCCESS}} = vars;

const PaymentSystemParams = ({paymentSystem, onClose, userRights}) => {
  const gridRef = useRef(null);
  const formRef = useRef(null);
  const lastLoadedItemsRef = useRef([]);

  const [isAddPopupVisible, setIsAddPopupVisible] = React.useState(false);
  const [addFormData, setAddFormData] = React.useState({
    ParamName: '',
    ParamValue: '',
    Remark: ''
  });
  const [reloadSeq, setReloadSeq] = useState(0);

  const reload = useCallback(() => {
    setReloadSeq((s) => s + 1);
  }, []);

  const haveEnoughRight = useMemo(() => {
    return userRights && userRights.includes('11');
  }, [userRights]);

  // Main Data Source
  const dataSource = useMemo(() => {
    const loadAll = async () => {
      try {
        if (!paymentSystem || !paymentSystem.ID) return [];

        const response = await apiRequest({
          operation: 'PaymentSystemParam/List',
          data: {
            Filters: {PaymentSystemID: paymentSystem.ID},
            Sorts: ['ParamName'],
            Page: -1,
          }
        });

        if (response.data.ResponseCode === SUCCESS) {
          const rawData = response.data.Response.PaymentSystemParam || [];
          return rawData.map((item) => ({
            ...item,
            LocalParamValue: item.ParamValue ?? item.TypeParamValue ?? '',
            LocalRemark: item.Remark ?? item.TypeRemark ?? '',
            IsDelete: 0
          }));
        } else {
          notify(response.data.ResponseText, 'error', 3000);
          return [];
        }
      } catch (err) {
        notify('Network error', 'error', 3000);
        return [];
      }
    };

    return new CustomStore({
      key: 'ID',
      load: async () => {
        const items = await loadAll();
        lastLoadedItemsRef.current = items;
        return items;
      },
      byKey: async (key) => {
        const fromLastLoad = lastLoadedItemsRef.current.find((item) => String(item.ID) === String(key));
        if (fromLastLoad) return fromLastLoad;
        const items = await loadAll();
        lastLoadedItemsRef.current = items;
        return items.find((item) => String(item.ID) === String(key));
      },
    });
  }, [paymentSystem?.ID, reloadSeq]);

  const refreshGrid = useCallback(() => {
    reload();
    if (gridRef.current) {
      gridRef.current.instance().refresh();
    }
  }, [reload]);

  const onAddPopupHidden = () => {
    setIsAddPopupVisible(false);
    setAddFormData({ParamName: '', ParamValue: '', Remark: ''});
  };

  // Add individual parameter
  const saveAddForm = async () => {
    const isValid = formRef.current.instance().validate().isValid;
    if (!isValid) return;

    try {
      const payload = [{
        PaymentSystemID: paymentSystem.ID,
        ParamName: addFormData.ParamName,
        ParamValue: addFormData.ParamValue,
        Remark: addFormData.Remark ?? '',
        IsDelete: 0
      }];

      const response = await apiRequest({
        operation: 'PaymentSystemParam/Mod',
        data: {
          Params: {
            PaymentSystemParam: payload
          }
        }
      });

      if (response.data.ResponseCode === SUCCESS) {
        notify('Успешно добавлено', 'success', 2000);
        onAddPopupHidden();
        refreshGrid();
      } else {
        notify(response.data.ResponseText, 'error', 3000);
      }
    } catch (err) {
      notify('Ошибка добавления', 'error', 3000);
    }
  };

  // Main Save workflow (Batch editing)
  const onSaving = async (e) => {
    e.cancel = true; // Prevent default DevExtreme save behavior

    const changes = e.changes;
    if (!changes.length) {
      return;
    }

    try {
      const store = e.component?.getDataSource?.()?.store?.();

      const payload = await Promise.all(
        changes.map(async (change) => {
          const original = (await store?.byKey?.(change.key).catch(() => null)) || {};
          const fullData = {...original, ...change.data, ID: change.key};

          const isDelete = fullData.IsDelete === 1 || fullData.IsDelete === true;
          let finalParamValue = fullData.LocalParamValue;
          if (isDelete) {
            finalParamValue = '';
          }

          return {
            ID: fullData.ID,
            PaymentSystemID: paymentSystem.ID,
            ParamName: fullData.ParamName,
            ParamValue: finalParamValue,
            Remark: fullData.LocalRemark ?? '',
            IsDelete: isDelete ? 1 : 0
          };
        }),
      );

      const response = await apiRequest({
        operation: 'PaymentSystemParam/Mod',
        data: {
          Params: {
            PaymentSystemParam: payload
          }
        }
      });

      if (response.data.ResponseCode === SUCCESS) {
        e.component.cancelEditData(); // clear batch changes
        notify('Сохранено', 'success', 2000);
        refreshGrid(); // keep popup open, just reload list
      } else {
        notify(response.data.ResponseText, 'error', 3000);
      }
    } catch (err) {
      notify('Ошибка сохранения', 'error', 3000);
    }
  };

  return (
    <div className="payment-system-params">

      {/* Nested Add Pop-up */}
      <Popup
        visible={isAddPopupVisible}
        onHiding={onAddPopupHidden}
        title={`Добавить параметр платежной системы ${paymentSystem?.ID || ''}`}
        width={450}
        height="auto"
        showCloseButton={true}
      >
        <div style={{padding: '10px'}}>
          <Form
            ref={formRef}
            formData={addFormData}
            labelLocation="left"
          >
            <SimpleItem dataField="ParamName" label={{text: 'Параметр'}} isRequired={true}/>
            <SimpleItem dataField="ParamValue" label={{text: 'Значение'}} isRequired={true}/>
            <SimpleItem dataField="Remark" label={{text: 'Примечание'}}/>
          </Form>
          <div style={{marginTop: '20px', textAlign: 'right'}}>
            <Button
              text="Сохранить"
              type="success"
              onClick={saveAddForm}
              style={{marginRight: '10px'}}
            />
            <Button
              text="Отмена"
              onClick={onAddPopupHidden}
            />
          </div>
        </div>
      </Popup>

      <div className="payment-system-params__grid">
        <DataGrid
          ref={gridRef}
          dataSource={dataSource}
          keyExpr="ID"
          width="100%"
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
          columnAutoWidth={false}
          repaintChangesOnly={true}
          wordWrapEnabled={true}
          onSaving={onSaving}
        >
          <Editing
            mode="batch"
            allowUpdating={haveEnoughRight}
            selectTextOnEditStart={true}
            startEditAction="click"
          />

          <FilterRow visible={true}/>
          <Scrolling mode="infinite" showScrollbar="always" useNative={false}/>

          <Toolbar>
            <Item location="before">
              <Button
                icon="add"
                text="Добавить"
                visible={haveEnoughRight}
                onClick={() => setIsAddPopupVisible(true)}
              />
            </Item>
            <Item location="before">
              <Button text="Обновить" onClick={refreshGrid}/>
            </Item>
          </Toolbar>

          <Column dataField="ParamName" caption="Параметр" allowEditing={false} width={250}/>
          <Column dataField="LocalParamValue" caption="Значение" width={400}/>
          <Column dataField="LocalRemark" caption="Примечание" minWidth={350}/>
          <Column
            dataField="IsDelete"
            caption="Удалить"
            dataType="boolean"
            fixed={true}
            fixedPosition={"right"}
            width={100}
            calculateCellValue={(rowData) => rowData.IsDelete === 1}
            setCellValue={(rowData, value) => {
              rowData.IsDelete = value ? 1 : 0;
            }}
          />
        </DataGrid>
      </div>

      <div className="payment-system-params__actions">
        <Button
          text="Сохранить"
          type="success"
          visible={haveEnoughRight}
          onClick={() => {
            if (gridRef.current) {
              gridRef.current.instance().saveEditData();
            }
          }}
        />
        <Button
          text="Отмена"
          onClick={onClose}
        />
      </div>
    </div>
  );
};

export default PaymentSystemParams;
